import { FunctionComponent, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { useNavbarItems } from '@/hooks';
import { Notifications } from '../Navbar/components';
import { Rail } from './Rail';
import { RailHelpButton } from './components';
import { buildRailSections, RAIL_HELP_LABEL } from './railSections';
import { RailSkeleton } from './RailSkeleton';
import { usePendingPathname } from '../Router/pendingNavigation';
import { warmRouteChunksWhenIdle } from '@/perf/routePreload';
import { getPlanDisplayName } from '@/utils/plans';

const displayName = (session: ReturnType<typeof useAuth>['session']): string => {
  const first = session?.first_name || '';
  const last = session?.last_name || '';
  const full = `${first} ${last}`.trim();
  return full || session?.nickname || session?.email || 'Ágora';
};

const RAIL_SETTINGS_LABEL = 'Ajustes generales';

export const RailContainer: FunctionComponent = () => {
  const { session, logOut } = useAuth();
  const pathname = useLocation().pathname;
  // Mientras la ruta destino carga, el ítem tocado ya se marca activo: la ruta
  // confirmada sigue siendo la vieja hasta que llega el chunk.
  const pendingPathname = usePendingPathname();
  const { mainItems, dropdownItems } = useNavbarItems(pendingPathname ?? pathname);

  // Ajustes generales se abre tocando el bloque de avatar, nombre y plan (no va en la lista).
  const settingsItem = dropdownItems.find((item) => item.label === RAIL_SETTINGS_LABEL);
  const sections = useMemo(
    () =>
      buildRailSections(
        mainItems,
        dropdownItems.filter((item) => item.label !== RAIL_SETTINGS_LABEL)
      ),
    [mainItems, dropdownItems]
  );
  const helpItem = mainItems.find((item) => item.label === RAIL_HELP_LABEL);

  // Destinos del rail precalentados de a uno con el navegador ocioso (solo desktop): los
  // probables desde la pantalla actual apenas pinta su contenido, el resto con la página
  // asentada. Vuelve a programarse en cada navegación (cambian los destinos probables).
  // La clave en string evita reprogramar si se rearman los ítems sin cambiar los destinos.
  const railLinks = useMemo(
    () =>
      sections
        .flatMap((section) => section.items)
        .flatMap((item) => ('customKey' in item ? [] : [item, ...(item.dropdownContent ?? [])]))
        // Solo rutas internas (las externas empiezan con http) que navegan de verdad.
        .filter((item) => item.link?.startsWith('/') && !item.locked && !item.disabled)
        .map((item) => item.link)
        .join('|'),
    [sections]
  );
  const hasSession = !!session;
  useEffect(() => {
    if (!hasSession || !railLinks) return;
    return warmRouteChunksWhenIdle(railLinks.split('|'), pathname);
  }, [hasSession, railLinks, pathname]);

  // Sin sesión todavía (arranque): el rail provisional ocupa el mismo lugar.
  if (!session) return <RailSkeleton />;

  const avatar = session.avatar_url ? `${session.avatar_url}_250.webp` : undefined;
  const onHome = session.is_staff ? '/agenda' : '/';
  // Nombre comercial, nunca el `plan_name` crudo (salía "pro · professional" a un Plan Negocio).
  const planLabel = session.is_staff
    ? 'Equipo'
    : getPlanDisplayName(session.vendor?.plan_name, session.is_venue);

  return (
    <Rail
      onHome={onHome}
      avatar={avatar}
      name={displayName(session)}
      planLabel={planLabel}
      profileLink={
        settingsItem?.link ? { href: settingsItem.link, active: !!settingsItem.isSelected } : undefined
      }
      sections={sections}
      onLogout={logOut}
      headerActions={(collapsed) => (
        <>
          {helpItem && <RailHelpButton item={helpItem} collapsed={collapsed} />}
          <Notifications variant="rail" collapsed={collapsed} />
        </>
      )}
    />
  );
};
